import { computed, h, ref, watch } from 'vue';
import { Button as AButton } from 'ant-design-vue/es/components';
import { KpiNamePopover } from '@/share/components';
import { CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { getKpiCompareModeText } from '@/constants/kpi';
import { VxeButton } from 'vxe-pc-ui';

export const useKpiTableColumns = (onAction, extraColumns = []) => {
    const zhKpiLevels = [ '零', '一', '二', '三', '四', '五', '六', '七', '八', '九' ];

    const layers = ref(3);
    const editMode = ref(false);

    const staticColumns = [
        {
            title: '指标值',
            dataIndex: [ 'data', 'value' ],
            width: 130,
            resizable: true,
            align: 'center',
            customRender: ({ record }) => {
                const { compareMode = 0, value = 0, nValue = '', unit = '' } = record.data || {};
                const compareText = getKpiCompareModeText(compareMode);
                if (compareMode === 0) {
                    return '';
                }
                if (compareMode === 1) {
                    return '不使用';
                }
                if (compareMode === 2) {
                    return nValue;
                }
                return `${compareText} ${value} ${unit}`;
            },
        },
        {
            title: '指标值说明',
            dataIndex: [ 'data', 'summary' ],
            align: 'center',
            width: 260,
            resizable: true,
            customRender: ({ record }) => {
                const { summary = '', explain = '' } = record.data || {};
                const lines = [];
                if (explain) {
                    let szExplain = explain;
                    if (!szExplain.endsWith('。')) {
                        szExplain += '。';
                    }
                    lines.push(h('span', null, szExplain));
                }
                if (summary) {
                    if (explain) {
                        lines.push((h('br', null)));
                    }
                    lines.push(h('span', null, summary));
                }
                return h('span', null, [
                    ...lines,
                ]);
            },
        },
        ...extraColumns,
    ];

    // action: edit, add, remove
    const handleAction = (action, id, level) => {
        !!onAction && onAction(action, id, level);
    };

    const renderDynamicColumn = (col) => ({ text, record }) => {
        if (!editMode.value) return text;
        const data = record.parents[col] || {};
        if (data.type === 'action') {
            return h(AButton, {
                type: 'link',
                onClick: () => handleAction('add', data.parentId, data.level),
            }, {
                default: () => '添加',
                icon: <EditOutlined />,
            });
        } else if (data.type === 'empty') {
            return '';
        }
        // 1级指标编辑菜单
        if (col === 0) {
            return h(KpiNamePopover, {
                editMode: true,
                label: text,
                popover: true,
                onEdit: () => handleAction('edit', data.id, data.level),
                onRemove: () => handleAction('remove', data.id, data.level),
                actions: [
                    {
                        title: '编辑',
                        icon: h(EditOutlined),
                        action: () => handleAction('edit', data.id, data.level),
                    },
                    {
                        title: '添加下级',
                        icon: h(PlusOutlined),
                        action: () => handleAction('add', data.id, data.level + 1),
                    },
                ],
            });
        }
        // 2级指标菜单
        return h(KpiNamePopover, {
            editMode: true,
            label: text,
            onEdit: () => handleAction('edit', data.id, data.level),
            onRemove: () => handleAction('remove', data.id, data.level),
            actions: [
                {
                    title: '编辑',
                    icon: h(EditOutlined),
                    action: () => handleAction('edit', data.id, data.level),
                },
                {
                    title: 'AI生成',
                    icon: h(PlusOutlined),
                    action: () => handleAction('ai-create-items', data.id, data.level + 1),
                },
                {
                    title: '添加下级',
                    icon: h(PlusOutlined),
                    action: () => handleAction('add', data.id, data.level + 1),
                },
                {
                    title: '删除',
                    danger: true,
                    icon: h(CloseOutlined),
                    action: () => handleAction('remove', data.id, data.level),
                },
            ],
        });
    };

    const renderStaticColumn = ({ text, record }) => {
        if (!editMode.value) return text;

        if (record.type === 'action') {
            return h(VxeButton, {
                mode: 'text',
                status: 'primary',
                icon: 'vxe-icon-add',
                onClick: () => handleAction('add', record.parentId, record.level),
            }, {
                default: () => '添加指标项',
            });
        } else if (record.type === 'empty') {
            return '';
        }
        return h(KpiNamePopover, {
            editMode: true,
            label: text,
            popover: true,
            onEdit: () => handleAction('edit', record.id, record.level),
            onRemove: () => handleAction('remove', record.id, record.level),
            actions: [
                {
                    title: '编辑',

                    icon: h(EditOutlined),
                    action: () => handleAction('edit', record.id, record.level),
                },
                {
                    title: '删除',
                    danger: true,
                    icon: h(CloseOutlined),
                    action: () => handleAction('remove', record.id, record.level),
                },
            ],
        });
    };

    const dynamicColumnWidths = [ 160, 180, 200, 220, 240, 260 ];
    const dynamicColumns = computed(() => {
        const list = [];
        for (let i = 1; i < layers.value; i++) {
            list.push({
                title: `${zhKpiLevels[i]}级指标`,
                dataIndex: [ 'parents', `${i - 1}`, 'data', 'name' ],
                width: dynamicColumnWidths[i - 1],
                align: 'center',
                resizable: true,
                customCell: (row) => {
                    const { parents = [] } = row || {};
                    const { rowSpan = 1 } = parents[i - 1] || {};
                    return { rowSpan };
                },
                customRender: renderDynamicColumn(i - 1),
            });
        }
        list.push(
            {
                title: `${zhKpiLevels[layers.value]}级指标`,
                dataIndex: [ 'data', 'name' ],
                width: 210,
                resizable: true,
                align: 'center',
                customRender: renderStaticColumn,
            },
        );
        return list;
    });

    const columns = ref([
        ...dynamicColumns.value,
        ...staticColumns,
    ]);

    watch(layers, (val) => {
        columns.value = [
            ...dynamicColumns.value,
            ...staticColumns,
        ];
    });

    const handleTableColumnResize = (w, col) => {
        if (!Number.isNaN(w)) {
            col.width = w;
        }
    };

    return {
        columns,
        layers,
        editMode,
        handleTableColumnResize,
    };
};
